import { useState, useCallback } from "react";
import { attendanceAPI } from "../services/api";
import { errorMessage } from "../services/platformClient";
import { getPosition, compressImage, reverseGeocode } from "../utils/helpers";
import { clearCache } from "../utils/cache";
import { buildAttendanceTimePayload } from "../utils/timezone";


// Ditampilkan saat absen gagal terkirim karena jaringan/server sibuk tapi sudah
// tersimpan aman di HP (antrean) dan akan dikirim ulang otomatis.
const QUEUED_MESSAGE = "Absen tersimpan di HP. Akan dikirim otomatis saat koneksi pulih.";

export default function useAttendance() {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(null);
  const [info, setInfo]       = useState(null); // absen tersimpan di HP, menunggu dikirim
  const [gpsStatus, setGpsStatus] = useState("idle"); // idle | detecting | ok | error

  // Fungsi internal untuk ambil GPS secara mandiri
 
  const captureGPS = useCallback(async () => {
    setGpsStatus("detecting");
    console.log("📍 [GPS] Start detecting...");

    try {
      const pos = await getPosition();

      console.log("📍 [GPS] SUCCESS:", {
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      });

      setGpsStatus("ok");

      return { 
        latitude: pos.coords.latitude, 
        longitude: pos.coords.longitude 
      };
    } catch (err) {
      console.error("❌ [GPS] ERROR:", err);
      setGpsStatus("error");
      throw new Error("GPS gagal: Pastikan izin lokasi aktif.");
    }
  }, []);

  const doCheckIn = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setInfo(null);

    try {
      // Compress image sebelum upload (kurangi ukuran kirim)
      let image = null;
      if (payload.image) {
        image = await compressImage(payload.image);
      }

      // 🌍 Reverse Geocoding: Convert lat-long ke address
      let address = null;
      if (payload.latitude && payload.longitude) {
        address = await reverseGeocode(payload.latitude, payload.longitude);
      }

      const data = await attendanceAPI.checkIn({
        latitude:  payload.latitude,
        longitude: payload.longitude,
        work_mode: payload.workMode,
        address:   address,  // ← Kirim address ke backend
        ...buildAttendanceTimePayload(),
        image,
      });

      if (data?.queued) setInfo(QUEUED_MESSAGE);
      else setSuccess("Check In berhasil!");
      // Kembalikan data response (check_in, checkin_image_url, dll)
      return data?.data ?? data;

    } catch (err) {
      setError(errorMessage(err, "Check In gagal"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const doCheckOut = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setInfo(null);

    try {
      let image = null;
      if (payload.image) {
        image = await compressImage(payload.image);
      }

      // 🌍 Reverse Geocoding: Convert lat-long ke address
      let address = null;
      if (payload.latitude && payload.longitude) {
        address = await reverseGeocode(payload.latitude, payload.longitude);
      }

      const data = await attendanceAPI.checkOut({
        latitude:  payload.latitude,
        longitude: payload.longitude,
        address:   address,  // ← Kirim address ke backend
        ...buildAttendanceTimePayload(),
        image,
      });

      if (data?.queued) setInfo(QUEUED_MESSAGE);
      else setSuccess("Check Out berhasil!");
      clearCache("attendance_today");
      clearCache("attendance_history");
      return data?.data ?? data;

    } catch (err) {
      setError(errorMessage(err, "Check Out gagal"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    success,
    info,
    gpsStatus,
    captureGPS,
    doCheckIn,
    doCheckOut,
    setError,
    setSuccess,
    setInfo,
  };
}
